import React, { useState, useEffect } from 'react';
import { Formik, Form, Field, ErrorMessage } from 'formik';
import * as Yup from 'yup';
import { FiMapPin, FiCircle, FiSquare } from 'react-icons/fi';
import GeofenceMap from '../map/GeofenceMap';

// Validation schema for geofence form
const geofenceSchema = Yup.object().shape({
  name: Yup.string()
    .required('Name is required')
    .max(100, 'Name must be at most 100 characters'),
  description: Yup.string()
    .max(500, 'Description must be at most 500 characters'),
  type: Yup.string()
    .required('Type is required')
    .oneOf(['circle', 'polygon'], 'Invalid geofence type'),
  alertType: Yup.string()
    .required('Alert type is required')
    .oneOf(['entry', 'exit', 'both'], 'Invalid alert type'),
  isActive: Yup.boolean()
});

const GeofenceForm = ({ geofence, onSubmit, isSubmitting = false }) => {
  // State for map-related data
  const [mapData, setMapData] = useState(null);
  const [geofenceType, setGeofenceType] = useState(geofence?.type || 'circle');
  
  // Initial form values
  const initialValues = {
    name: geofence?.name || '',
    description: geofence?.description || '',
    type: geofence?.type || 'circle',
    alertType: geofence?.alertType || 'both',
    isActive: geofence?.isActive !== undefined ? geofence.isActive : true
  };
  
  // Initialize map data from existing geofence if available
  useEffect(() => {
    if (geofence) {
      if (geofence.type === 'circle') {
        setMapData({
          type: 'circle',
          center: geofence.center,
          radius: geofence.radius
        });
      } else if (geofence.type === 'polygon') {
        setMapData({
          type: 'polygon',
          vertices: geofence.vertices
        });
      }
    }
  }, [geofence]);
  
  // Handle map data changes
  const handleMapChange = (newData) => {
    setMapData(newData);
  };
  
  // Handle form submission
  const handleSubmit = (values) => {
    // Make sure we have the required map data
    if (!mapData) {
      alert('Please define the geofence area on the map');
      return;
    }
    
    if (values.type === 'circle' && (!mapData.center || !mapData.radius)) {
      alert('Please define a circle on the map');
      return;
    }
    
    if (values.type === 'polygon' && (!mapData.vertices || mapData.vertices.length < 3)) {
      alert('Please define a polygon with at least 3 points on the map');
      return;
    }
    
    // Combine form values with map data
    const geofenceData = {
      ...values,
      ...mapData
    };
    
    // Submit to parent component
    onSubmit(geofenceData);
  };
  
  return (
    <Formik
      initialValues={initialValues}
      validationSchema={geofenceSchema}
      onSubmit={handleSubmit}
      enableReinitialize
    >
      {({ values, errors, touched, setFieldValue }) => (
        <Form className="space-y-6">
          {/* Basic Information */}
          <div className="bg-white shadow rounded-md p-6">
            <h3 className="text-lg font-semibold mb-4">Basic Information</h3>
            
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              {/* Name */}
              <div>
                <label htmlFor="name" className="form-label">Name</label>
                <Field
                  id="name"
                  name="name"
                  type="text"
                  className={`form-input ${errors.name && touched.name ? 'border-ui-error' : ''}`}
                  placeholder="Enter geofence name"
                />
                <ErrorMessage name="name" component="div" className="form-error" />
              </div>
              
              {/* Description */}
              <div>
                <label htmlFor="description" className="form-label">Description (Optional)</label>
                <Field
                  id="description"
                  name="description"
                  as="textarea"
                  rows="2"
                  className={`form-input ${errors.description && touched.description ? 'border-ui-error' : ''}`}
                  placeholder="Enter geofence description"
                />
                <ErrorMessage name="description" component="div" className="form-error" />
              </div>
            </div>
          </div>
          
          {/* Geofence Type */}
          <div className="bg-white shadow rounded-md p-6">
            <h3 className="text-lg font-semibold mb-4">Geofence Type</h3>
            
            <div className="space-y-4">
              {/* Type selector */}
              <div>
                <div className="flex space-x-4">
                  <label className={`
                    flex items-center p-4 border rounded-md cursor-pointer
                    ${values.type === 'circle' ? 'border-primary bg-primary bg-opacity-10' : 'border-gray-300'}
                  `}>
                    <input
                      type="radio"
                      name="type"
                      value="circle"
                      checked={values.type === 'circle'}
                      onChange={() => {
                        setFieldValue('type', 'circle');
                        setGeofenceType('circle');
                      }}
                      className="sr-only"
                    />
                    <FiCircle className={`mr-2 ${values.type === 'circle' ? 'text-primary' : 'text-gray-500'}`} size={20} />
                    <div>
                      <p className={`font-medium ${values.type === 'circle' ? 'text-primary' : 'text-gray-700'}`}>Circle</p>
                      <p className="text-sm text-gray-500">Define a circular area</p>
                    </div>
                  </label>
                  
                  <label className={`
                    flex items-center p-4 border rounded-md cursor-pointer
                    ${values.type === 'polygon' ? 'border-primary bg-primary bg-opacity-10' : 'border-gray-300'}
                  `}>
                    <input
                      type="radio"
                      name="type"
                      value="polygon"
                      checked={values.type === 'polygon'}
                      onChange={() => {
                        setFieldValue('type', 'polygon');
                        setGeofenceType('polygon');
                      }}
                      className="sr-only"
                    />
                    <FiSquare className={`mr-2 ${values.type === 'polygon' ? 'text-primary' : 'text-gray-500'}`} size={20} />
                    <div>
                      <p className={`font-medium ${values.type === 'polygon' ? 'text-primary' : 'text-gray-700'}`}>Polygon</p>
                      <p className="text-sm text-gray-500">Define a custom shape</p>
                    </div>
                  </label>
                </div>
                <ErrorMessage name="type" component="div" className="form-error mt-1" />
              </div>
              
              {/* Map for drawing geofence */}
              <div className="h-80 bg-gray-100 rounded">
                <GeofenceMap 
                  geofence={mapData || { type: geofenceType }} 
                  editable={true}
                  onChange={handleMapChange}
                />
              </div>
              
              {/* Map data display */}
              {mapData && (
                <div className="p-4 bg-gray-50 rounded-md text-sm">
                  {mapData.type === 'circle' && mapData.center && mapData.radius && (
                    <div>
                      <p className="font-medium">Circle Geofence</p>
                      <p>Center: {mapData.center.lat.toFixed(6)}, {mapData.center.lng.toFixed(6)}</p>
                      <p>Radius: {mapData.radius.toFixed(2)} meters</p>
                    </div>
                  )}
                  
                  {mapData.type === 'polygon' && mapData.vertices && (
                    <div>
                      <p className="font-medium">Polygon Geofence with {mapData.vertices.length} vertices</p>
                      <div className="max-h-28 overflow-y-auto">
                        {mapData.vertices.map((vertex, index) => (
                          <p key={index} className="font-mono">
                            {index + 1}: {vertex.lat.toFixed(6)}, {vertex.lng.toFixed(6)}
                          </p>
                        ))}
                      </div>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
          
          {/* Alert Settings */}
          <div className="bg-white shadow rounded-md p-6">
            <h3 className="text-lg font-semibold mb-4">Alert Settings</h3>
            
            <div className="space-y-4">
              {/* Alert Type */}
              <div>
                <label className="form-label">Alert Type</label>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mt-1">
                  <label className={`
                    flex items-center p-3 border rounded-md cursor-pointer
                    ${values.alertType === 'entry' ? 'border-primary bg-primary bg-opacity-10' : 'border-gray-300'}
                  `}>
                    <Field
                      type="radio"
                      name="alertType"
                      value="entry"
                      className="mr-2"
                    />
                    <div>
                      <p className="font-medium">Entry</p>
                      <p className="text-sm text-gray-500">Alert when vehicles enter</p>
                    </div>
                  </label>
                  
                  <label className={`
                    flex items-center p-3 border rounded-md cursor-pointer
                    ${values.alertType === 'exit' ? 'border-primary bg-primary bg-opacity-10' : 'border-gray-300'}
                  `}>
                    <Field
                      type="radio"
                      name="alertType"
                      value="exit"
                      className="mr-2"
                    />
                    <div>
                      <p className="font-medium">Exit</p>
                      <p className="text-sm text-gray-500">Alert when vehicles exit</p>
                    </div>
                  </label>
                  
                  <label className={`
                    flex items-center p-3 border rounded-md cursor-pointer
                    ${values.alertType === 'both' ? 'border-primary bg-primary bg-opacity-10' : 'border-gray-300'}
                  `}>
                    <Field
                      type="radio"
                      name="alertType"
                      value="both"
                      className="mr-2"
                    />
                    <div>
                      <p className="font-medium">Both</p>
                      <p className="text-sm text-gray-500">Alert on entry and exit</p>
                    </div>
                  </label>
                </div>
                <ErrorMessage name="alertType" component="div" className="form-error mt-1" />
              </div>
              
              {/* Active Status */}
              <div className="flex items-center">
                <Field
                  type="checkbox"
                  name="isActive"
                  id="isActive"
                  className="h-4 w-4 text-primary focus:ring-primary border-gray-300 rounded"
                />
                <label htmlFor="isActive" className="ml-2 block text-sm text-gray-700">
                  Activate geofence immediately
                </label>
              </div>
            </div>
          </div>
          
          {/* Submit button */}
          <div className="flex justify-end">
            <button
              type="submit"
              disabled={isSubmitting}
              className={`btn btn-primary ${isSubmitting ? 'opacity-75 cursor-not-allowed' : ''}`}
            >
              {isSubmitting ? 'Saving...' : geofence ? 'Update Geofence' : 'Create Geofence'}
            </button>
          </div>
        </Form>
      )}
    </Formik>
  );
};

export default GeofenceForm;